import { isLoggedIn } from '@/api/api.auth';
import { callApi } from '@/api/api.config';
import { BreadcrumbData } from '@/components/breadcrumbs/breadcrumbs.props';
import { routes } from '@/modules/Router';
import { UrlParams, validateAndGetCorrectUrl } from '@/modules/Router/Router.hooks';
import { LoaderFunctionArgs, redirect } from 'react-router-dom';

export const protectedLoader =
  () =>
  ({ request }: LoaderFunctionArgs) => {
    const isUserLoggedIn = isLoggedIn();

    if (!isUserLoggedIn) {
      const params = new URLSearchParams();
      params.set('from', new URL(request.url).pathname);
      return redirect(`${routes.login}?${params.toString()}`);
    }
    return null;
  };

const fetchProjectData = async (projectId?: string) => {
  if (!projectId) throw new Error('Project ID is required');
  return callApi({ method: 'GET', url: `customer/projects/${projectId}/` }).then((response) => response.data);
};

const fetchFarmData = async (farmId?: string) => {
  if (!farmId) throw new Error('Farm ID is required');
  return callApi({ method: 'GET', url: `farm/summary/farm/${farmId}/` }).then((response) => response.data);
};

const fetchFieldData = async (fieldId?: string) => {
  if (!fieldId) throw new Error('Field ID is required');
  return callApi({ method: 'GET', url: `farm/summary/field/${fieldId}/` }).then((response) => response.data);
};

type DataFetcher<T> = (id?: string) => Promise<{ breadcrumb?: BreadcrumbData } & T>;

export const protectedLoaderWithValidation =
  <T>(dataFetcher: DataFetcher<T>, idExtractor: (params: UrlParams) => string | undefined) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const authResult = protectedLoader()({ request, params, context: {} });
    if (authResult) return authResult;

    const id = idExtractor(params as UrlParams);
    if (!id) return null;

    try {
      const data = await dataFetcher(id);
      const breadcrumbData = data.breadcrumb;

      if (!breadcrumbData) return null;

      const currentPath = new URL(request.url).pathname;
      const correctUrl = validateAndGetCorrectUrl(params as UrlParams, breadcrumbData, currentPath);

      if (correctUrl) {
        return redirect(correctUrl);
      }

      return null;
    } catch (error) {
      return null;
    }
  };

export const projectLoader = protectedLoaderWithValidation(fetchProjectData, (params) => params.projectId);

export const farmLoader = protectedLoaderWithValidation(fetchFarmData, (params) => params.farmId);

export const fieldLoader = protectedLoaderWithValidation(fetchFieldData, (params) => params.fieldId);
